import { useLoaderData } from 'react-router';
import { ArrowUpRight } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import type { loader } from '~/routes/_app.product.$urlSegment';

/**
 * Compact supplier trust row shown directly under the mobile CTA cluster —
 * the standard circular logo badge, the brand name, its location and the
 * ArchiPro-only proof point "Specified in N projects". People enquire when
 * they trust the counterparty, so the strongest trust signal sits beside the
 * primary action rather than in the collapsed About the Supplier row five
 * sections down. The full supplier block still lives in the accordion.
 *
 * Uses the shared editorial language of the revamp: the `ProfessionalTileLogo`
 * badge (with its contrast border for white-on-white logos), the ink/grey type
 * palette and a hairline-bordered container — no coloured callouts.
 */
export const MobileSupplierTrustRow = () => {
    const { product, featuredProjects } = useLoaderData<typeof loader>();
    const professional = product.Professional;
    if (!professional?.Title) return null;

    // The product's category as the supplier descriptor — universal (every
    // product has one), unlike a location, which many suppliers lack (brands)
    // or have several of.
    const category = product.Categories?.[0]?.PrettyTitle ?? '';

    const projectCount =
        featuredProjects?.FeaturedProjects?.pageInfo?.totalCount ?? 0;

    return (
        <RoccoLink
            to={professional.Link}
            className="group flex items-center gap-3 py-4"
        >
            {professional.CompanyLogo ? (
                <ProfessionalTileLogo
                    image={{
                        src: professional.CompanyLogo,
                        alt: `${professional.Title} logo`,
                    }}
                    background={professional.LogoBackgroundColor ?? null}
                    className="size-12 shrink-0"
                    contrastBorder
                />
            ) : null}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-1.5">
                    <span className="truncate font-sans text-base leading-[1.2] font-medium tracking-[-0.02em] text-[#1f1f21]">
                        {professional.Title}
                    </span>
                    <ArrowUpRight className="size-4 shrink-0 text-[#1f1f21] transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
                {category ? (
                    <span className="truncate font-sans text-[0.8125rem] leading-[1.2] text-[#767676]">
                        {category}
                    </span>
                ) : null}
                {projectCount > 0 ? (
                    <span className="font-sans text-[0.8125rem] leading-[1.2] text-[#1f1f21]">
                        <span className="font-medium">
                            Specified in {projectCount}{' '}
                            {projectCount === 1 ? 'project' : 'projects'}
                        </span>{' '}
                        <span className="text-[#767676]">on ArchiPro</span>
                    </span>
                ) : null}
            </span>
        </RoccoLink>
    );
};
